import { CommonModule } from '@angular/common';
import { Component, EnvironmentInjector, EventEmitter, Inject, Input, Output } from '@angular/core';
import { PortalApiService, HttpValidationProblemDetails, Advisor, FileParameter, MediaTag, Photo, PhotoResultSet } from 'src/app/services/portal-api.service';
import { forkJoin, mergeMap, Observable, of, Subject, take, switchMap, tap, EMPTY, from, concatMap, finalize, catchError } from 'rxjs';
import { SharedModule } from 'src/app/shared/shared.module';
import { DragDropModule } from '@angular/cdk/drag-drop'
import { ModalService } from '@indice/ng-components';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { PropertyListingTab } from 'src/app/features/property-listing/property-listing.component';
import {
  PhotoTagModalComponent
} from 'src/app/features/property-listing/photos/photo-tag-modal/photo-tag-modal.component';
import { HeaderService } from 'src/app/services/header.service';
import { ToastType, ToasterService } from 'src/app/services/toaster.service';
import { LanguageRouterLinkDirective } from 'src/app/shared/directives/language-router-link.directive';
import { localizeRoute } from 'src/app/shared/utilities/localizeRoutes';

@Component({
  selector: 'app-photos',
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    DragDropModule,
    TranslocoDirective,
    RouterModule,
    LanguageRouterLinkDirective,
    TranslocoPipe
  ],
  templateUrl: './photos.component.html'
})
export class PhotosComponent {
  @Input() advisor: Advisor | undefined;

  @Input() hideComplete: boolean = false;
  @Input() selectedCoverImage = new Media({ isDefault: true })
  @Input() selectedSecondaryImages: Media[] = [];
  @Input() propertyId: string | undefined;
  @Output() onNextTab: Subject<PropertyListingTab> = new Subject();
  @Output() isComplete: Subject<boolean> = new Subject();
  @Output() onCoverSelection: EventEmitter<Media> = new EventEmitter()
  @Output() onSecondaryImageSelection: EventEmitter<Media[]> = new EventEmitter()

  public coverPhoto = new Media({ isDefault: true });
  public secondaryPhotos: Media[] = [];
  public propertyListingTab = PropertyListingTab;
  public coverDragover = false;
  public secondaryDragover = false;
  public propertyCode = '';
  protected uploadedFiles: Media[] = [];

  constructor(
    private _portalApi: PortalApiService,
    private _modalService: ModalService,
    private _activatedRoute: ActivatedRoute,
    private _headerService: HeaderService,
    private _toastService: ToasterService,
    private _router: Router,
    private _translocoService: TranslocoService,
    @Inject(EnvironmentInjector) private envInjector: EnvironmentInjector
  ) {

  }

  ngOnInit(): void {
    if (this.selectedCoverImage) this.coverPhoto = this.selectedCoverImage;
    if (this.selectedSecondaryImages) this.secondaryPhotos = this.selectedSecondaryImages;
    this._load();
    this._headerService.setAltNavigation(undefined, '/my-profile/listings/' + this.propertyCode + '/overview')

  }

  private _load() {
    this._portalApi.getPhotos(this.propertyId ?? '', true).pipe(
      tap((photos: PhotoResultSet) => {
        let coverPhoto = photos.items?.filter(x => x?.isDefault)[0];
        if (coverPhoto) {
          this.coverPhoto.mediaGuid = coverPhoto.mediaGuid;
          this.coverPhoto.previewSrc = `${coverPhoto.uri}?${Math.random()}`;

          let coverPhotoFilename = coverPhoto.uri?.substring(0, coverPhoto.uri.lastIndexOf('.'));
          this.coverPhoto.fallbackSrc = coverPhoto.photoItems?.filter(x => x.uri === `${coverPhotoFilename}.jpg` || x.uri === `${coverPhotoFilename}.jpeg`)[0]?.uri;
          if (this.coverPhoto.fallbackSrc) {
            this.coverPhoto.fallbackSrc = `${this.coverPhoto.fallbackSrc}?${Math.random()}`
          } else {
            this.coverPhoto.fallbackSrc = this.coverPhoto.previewSrc;
          }

          this.coverPhoto.tag = coverPhoto.tag;
        }

        let otherPhotos = photos.items?.filter(x => !x?.isDefault);
        if (otherPhotos) {
          this.secondaryPhotos = otherPhotos.map((x: Photo) => {
            let photoFilename = x.uri?.substring(0, x.uri.lastIndexOf('.'));

            return new Media({
              previewSrc: x.uri,
              fallbackSrc: x.photoItems?.filter(x => x.uri === `${photoFilename}.jpg` || x.uri === `${photoFilename}.jpeg`)[0]?.uri,
              order: x.order,
              mediaGuid: x.mediaGuid,
              isDefault: false,
              tag: x.tag
            })
          });
        }
      })).subscribe();
  }
  public onDragOver(event: any) {
    event.preventDefault();
  }

  public onCoverDropSuccess(event: any) {
    event.preventDefault();
    this.coverPhoto.file = event.dataTransfer.files[0];
    this.readFile(this.coverPhoto);
    this.coverDragover = false;
    this.onCoverSelection.emit(this.coverPhoto)
  }

  public onSecondaryDropSuccess(event: any) {
    event.preventDefault();
    let files: FileList | null = event.dataTransfer.files;
    Array.prototype.forEach.call(files, (file) => {
      let order = Math.max(...this.secondaryPhotos.map(x => x.order ?? 0), 0) + 1
      let media = new Media({ file: file, isDefault: false, order: order });
      this.readFile(media);
      if (media.file) {
        this.uploadedFiles.push(media);
        this.secondaryPhotos.push(media);
      }
    });
    this.secondaryDragover = false;
    this.onSecondaryImageSelection.emit(this.secondaryPhotos)
  }

  public onCoverPhotoSelected(event: any): void {
    this.coverPhoto.file = event.target.files[0];
    this.readFile(this.coverPhoto);
    this.onCoverSelection.emit(this.coverPhoto)
    this.uploadCoverPhoto();
  }

  public onSecondaryPhotosSelected(event: Event): void {
    const element = event.currentTarget as HTMLInputElement;
    let files: FileList | null = element.files;
    Array.prototype.forEach.call(files, (file) => {
      let order = Math.max(...this.secondaryPhotos.map(x => x.order ?? 0), 0) + 1
      let media = new Media({ file: file, isDefault: false, order: order });
      this.readFile(media);
      if (media.file) {
        this.uploadedFiles.push(media);
        this.secondaryPhotos.push(media);
      }
    });
    this.onSecondaryImageSelection.emit(this.secondaryPhotos);
    this.uploadSecondary(this.secondaryPhotos[this.secondaryPhotos.length - 1]);
  }

  public onDeletePhoto(image: Media) {
    if (image == this.coverPhoto) {
      this.coverPhoto = new Media({ isDefault: true });
      this.onCoverSelection.emit(this.coverPhoto)
    }
    if (this.secondaryPhotos.includes(image)) {
      const index = this.secondaryPhotos.indexOf(image);
      this.secondaryPhotos.splice(index, 1);
      this.onSecondaryImageSelection.emit(this.secondaryPhotos)
    }

    if (image.mediaGuid != null) {
      this._portalApi.deletePhoto(this.propertyId ?? '', image.mediaGuid).subscribe();
    }
  }

  public onTagAdd(photo: Media, isSecondary: boolean) {
    const tagModal = this._modalService.show(PhotoTagModalComponent, {
      animated: true,
      keyboard: true,
      class: 'h-dvh md:h-auto',
      initialState: {
        media: photo,
        propertyId: this.propertyId,
        isSecondary: isSecondary
      }
    });

    tagModal.onHidden?.pipe((take(1), mergeMap((r: any) => {
      if (r?.result?.tag) {
        photo.tag = r.result?.tag;
      } else if (r?.result?.delete && r?.result?.mediaGuid) {
        return of(this.onDeletePhoto(r?.result?.mediaGuid));
      }
      return of();
    }))).subscribe();
  }

  private uploadCoverPhoto() {
    if (this.coverPhoto.file && this.propertyId) {
      let fileParam: FileParameter = { data: this.coverPhoto.file, fileName: this.coverPhoto.file.name };
      this._portalApi.uploadPhoto(this.propertyId, fileParam, undefined, undefined, this.coverPhoto.isDefault, undefined).pipe(
        switchMap((res) => {
          this.coverPhoto.mediaGuid = res.mediaGuid;

          if (this.coverPhoto.tag && this.coverPhoto.mediaGuid) {
            return this._portalApi.updateMediaTag(this.propertyId ?? '', this.coverPhoto.mediaGuid, this.coverPhoto.tag as MediaTag)
          } else {
            return EMPTY;
          }
        }),
        catchError((error) => {
          let msg = ''
          try{
            const errorObj = error?.errors
            const keys = Object.keys(errorObj)
            if(keys){
              if(Array.isArray(errorObj?.[keys[0]])){
                msg = errorObj[keys[0]]?.[0]
              }
            } 
          }catch{
            msg = "Image is not valid"
          }
          this._toastService.showBanner(ToastType.Error, this._translocoService.translate(error?.title), msg, 6000); 
          this.coverPhoto =  new Media({ isDefault: true });        
          return of(null); 
        }),
      )
      .subscribe(() => {
        this._load();
      });
    }
  }

  private uploadSecondary(secondaryPhoto: any) {
    if (secondaryPhoto.file && this.propertyId) {

      let fileParam: FileParameter = { data: secondaryPhoto.file, fileName: secondaryPhoto.file.name };
      this._portalApi.uploadPhoto(this.propertyId, fileParam, undefined, undefined, secondaryPhoto.isDefault, secondaryPhoto.order).pipe(
        switchMap((res) => {
          secondaryPhoto.mediaGuid = res.mediaGuid;
          if (secondaryPhoto.tag && secondaryPhoto.mediaGuid) {
            return this._portalApi.updateMediaTag(this.propertyId ?? '', res.mediaGuid!, secondaryPhoto.tag as MediaTag)
          } else {
            return EMPTY;
          }
        }),
        catchError((error) => {
          let msg = ''
          try{
            const errorObj = error?.errors
            const keys = Object.keys(errorObj)
            if(keys){
              if(Array.isArray(errorObj?.[keys[0]])){
                msg = errorObj[keys[0]]?.[0]
              }
            } 
          } catch{
            msg = "Image is not valid"
          }
          this._toastService.showBanner(ToastType.Error, this._translocoService.translate(error?.title), msg, 6000);          
          return of(null); 
        }),
      )
      .subscribe(() => {
        this._load(); 
      });        
    }
  }

  protected completeAndExit() {
    let uploads: Observable<any>[] = [];

    

    

    let uploadErrors = false;
    from(uploads).pipe(
      concatMap((request) => request),
      catchError((err: HttpValidationProblemDetails) => {
        if (err.errors) {
          uploadErrors = true;
          this._toastService.showBanner(ToastType.Error, this._translocoService.translate('shared.toasts.photoUpload.fileNotAllowed'), err.errors['File'][0], 3000);
        }
        return EMPTY;
      }),
      finalize(() => {
        if (!uploadErrors) {
          this._router.navigate([localizeRoute('/my-profile/listings/' + this.propertyCode + '/overview', this.envInjector)], {});
        }
      })
    ).subscribe();
  }

  private readFile(photo: Media) {
    if (photo.file && /\.(jpg|jpeg)$/i.test(photo.file?.name)) {
      const reader = new FileReader();

      reader.addEventListener("load", () => {
        if (reader.result) {
          photo.previewSrc = reader.result.toString();
        }
      }, false);

      reader.readAsDataURL(photo.file)
    } else {
      this._toastService.showBanner(ToastType.Error, this._translocoService.translate('shared.toasts.photoUpload.fileNotAllowed'), this._translocoService.translate('shared.toasts.photoUpload.allowedTypes'), 3000);
      photo.file = undefined;
    }
  }
}

export class Media {
  file?: File | undefined;
  previewSrc?: string;
  fallbackSrc?: string;
  isDefault = false;
  order?: number | undefined;
  tag?: string | undefined;
  mediaGuid?: string;

  constructor(fields?: Media) {
    if (fields) Object.assign(this, fields);
  }
}
